<template>
    <v-navigation-drawer
            ref="navDrawer"
            v-model="menuVisible"
            fixed
            temporary
            clipped
            :width="320"
            @transitionend="onDrawerTransitionEnd">

        <v-list dense>
            <ul class="list-none pl-0">
                <li>
                    <v-subheader class="text--secondary font-weight-bold d-flex justify-space-between px-5 py-6">
                        <div>CODES</div>
                        <v-icon
                            ref="closeButton"
                            @click="closeMenu"
                            class="focus-buttons"
                            aria-label="Close the side menu"
                            tabindex="0"
                        >
                                mdi-close
                        </v-icon>
                    </v-subheader>
                    <ul class="list-none pl-0">
                        <li>
                            <v-list-item v-if="isLoggedIn" :href="getRoute('dashboard_landing')" aria-label="Go to Dashboard" class="focus-buttons">
                                <v-list-item-icon>
                                <v-icon>mdi-view-dashboard-outline</v-icon>
                                </v-list-item-icon>

                                <v-list-item-content>
                                    <v-list-item-title>Dashboard</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </li>
                        <li>
                            <v-list-group
                                prepend-icon="mdi-text-box-multiple-outline"
                                no-action
                                eager
                                class="focus-list-group">
                                <template v-slot:activator>
                                    <v-list-item-content>
                                        <v-list-item-title>Codes</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                                <ul class="list-none pl-0">
                                    <li v-for="(item, index) in codeItems">
                                        <v-list-item
                                        :key="index"
                                        :href="item.link"
                                        class="list-item-with-left-padding focus-buttons"
                                        :aria-label="`Go to ${item.title}`">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ item.title }}</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </li>
                                </ul>
                            </v-list-group>
                        </li>
                        <li>
                            <v-list-item :href="searchRoute" class="focus-buttons" aria-label="Go to Search">
                                <v-list-item-icon>
                                    <v-icon>mdi-magnify</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Search</v-list-item-title>
                            </v-list-item>
                        </li>
                        <li>
                            <v-list-group
                                v-if="isLoggedIn"
                                prepend-icon="mdi-bookmark-multiple-outline"
                                append-icon="mdi-chevron-down"
                                eager
                                class="focus-list-group"
                                :no-action="isPremiumUser">

                                <template v-slot:activator>
                                    <v-list-item-content>
                                        <v-list-item-title>Notes</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                                <ul class="list-none pl-0">
                                    <li v-for="(item, index) in userNotes" :key="index">
                                        <v-list-item
                                            v-if="!item.hideForEnterprise"
                                            :aria-label="`Go to ${item.title}`"
                                            :href="isPremiumUser ? item.link : ''"
                                            :data-tippy="isPremiumUser ? '' : 'Premium subscription required'"
                                            class="list-item-with-left-padding focus-buttons">
                                            <v-list-item-content>
                                                <v-list-item-title :class="isPremiumUser ? '' : 'grey--text text--lighten-1'">
                                                    {{ item.title }}
                                                </v-list-item-title>
                                            </v-list-item-content>
                                            <v-list-item-action v-if="!isPremiumUser">
                                                <v-avatar size="24" color="primaryLight">
                                                    <v-icon small>mdi-lock-open-variant-outline</v-icon>
                                                </v-avatar>
                                            </v-list-item-action>
                                        </v-list-item>
                                    </li>
                                </ul>
                            </v-list-group>
                        </li>
                    </ul>
                </li>
                <li>
                    <v-subheader class="text--secondary font-weight-bold px-5 pt-8 pb-6">CODE INTELLIGENCE</v-subheader>
                    <ul class="list-none pl-0">
                        <li>
                            <v-list-item :href="getRoute(isPremiumUser ? 'ai_navigator' : 'features_ai_navigator')" class="focus-buttons" aria-label="Go to AI Navigator">
                                <v-list-item-icon>
                                    <v-icon>mdi-navigation-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>AI Navigator</v-list-item-title>
                            </v-list-item>
                        </li>
                        <li>
                            <v-list-item :href="getRoute('quick_consult')" class="focus-buttons" aria-label="Go to Quick Consult">
                                <v-list-item-icon>
                                    <v-icon>mdi-comment-account-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Quick Consult
                                    <v-chip
                                        class="ml-2 primary--text cp"
                                        small
                                        color="primaryLight">
                                        New
                                    </v-chip>
                                </v-list-item-title>
                            </v-list-item>
                        </li>
                        <li>
                            <v-list-item :href="getRoute('premium_answers')" class="focus-buttons" aria-label="Go to Q&A Database">
                                <v-list-item-icon>
                                    <v-icon>mdi-forum-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Q&A Database</v-list-item-title>
                            </v-list-item>
                        </li>
                        <li>
                            <v-list-item :href="getRoute('compliance_calculators')" class="focus-buttons" aria-label="Go to Compliance Calculators">
                                <v-list-item-icon>
                                    <v-icon>mdi-calculator-variant-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Compliance Calculators</v-list-item-title>
                            </v-list-item>
                        </li>
                    </ul>
                </li>
                <template v-if="isLoggedIn">
                    <li>
                        <v-list-item class="mt-2 focus-buttons" aria-label="Go to Favorites">
                            <v-list-item-content>
                                <v-list-item-title class="text--secondary font-weight-bold">FAVORITES</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-btn role="button" icon small :href="getRoute('favorites_landing')" data-tippy="Manage Favorites" class="focus-buttons" aria-label="Click on Manage Favorites">
                                    <v-icon>mdi-cog-outline</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                        <ul class="list-none pl-0">
                            <li>
                                <div v-if="favoriteTitles.length === 0" class="text--secondary text-sm px-4 py-0">
                                    Add titles to your favorites to access them here
                                </div>

                                <v-list-item
                                    v-else
                                    v-for="(item, index) in favoriteTitles"
                                    :key="index"
                                    :href="item?.title?.link"
                                    :aria-label="`Go to ${item.title.display_title}`"
                                    class="fav-title-item focus-buttons">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.title.display_title }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </li>
                        </ul>
                    </li>
                </template>
                <li>
                    <v-subheader class="text--secondary font-weight-bold px-5 pt-8 pb-6">LEARN MORE</v-subheader>
                    <ul class="list-none pl-0">
                        <li>
                            <v-list-group
                                prepend-icon="mdi-creation"
                                append-icon="mdi-chevron-down"
                                no-action
                                class="focus-list-group">

                                <template v-slot:activator>
                                    <v-list-item-content>
                                        <v-list-item-title>Features & Pricing</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                                <ul class="list-none pl-0">
                                    <li v-for="(item, index) in featuresMenuItems">
                                        <v-list-item
                                        :key="index"
                                        :href="item.link"
                                        :aria-label="`Go to ${item.title}`"
                                        class="list-item-with-left-padding focus-buttons">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ item.title }}</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </li>
                                </ul>
                            </v-list-group>
                        </li>
                        <li>
                            <v-list-item :href="getRoute('concurrent-access')" class="focus-buttons" aria-label="Go to Solutions for Teams">
                                <v-list-item-icon>
                                    <v-icon>mdi-account-group-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Solutions for Teams</v-list-item-title>
                            </v-list-item>
                        </li>
                        <li>
                            <v-list-item :href="getRoute('user_exams')" class="focus-buttons" aria-label="Go to Exams">
                                <v-list-item-icon>
                                    <v-icon>mdi-school-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>Exams</v-list-item-title>
                            </v-list-item>
                        </li>
                    </ul>
                </li>
            </ul>
        </v-list>

        <template v-if="!isPremiumUser" v-slot:append>

            <v-divider class=""></v-divider>

            <div class="d-flex flex-column pa-2 pa-md-3">

                <v-btn
                        v-if="isGuestUser || !isPremiumUser"
                        block
                        color="accent2"
                        role="button"
                        aria-button="Go to Subscribe button"
                        class="font-weight-bold mb-2 focus-buttons"
                        :href="getRoute('pricing_page')"
                >
                    Subscribe
                </v-btn>

                <start-free-trial outlined color="black"></start-free-trial>

            </div>
        </template>

    </v-navigation-drawer>
</template>

<script>
import {ApiService} from "../../api/ApiService";
import {EventNames} from "../../classes/EventNames";
import {DomUtils} from "../../util/DomUtils";
import StartFreeTrial from "../buttons/StartFreeTrial.vue";
import {Shortcuts} from "../../Shortcuts";
import { getTopLevelOrderedCategories } from "../codes/Utils/CategorySorting";

const MAX_FAVORITES_COUNT = 9999;
const MAX_FAVORITE_TITLE_LENGTH = 40;

export default {
    components: {StartFreeTrial},
    data() {
        return {
            menuVisible: false,
            featuresMenuItems: [
                {title: 'What’s New', link: this.getRoute('news_updates')},
                {title: "Premium Features", link: this.getRoute('premium_features')},
                {title: "AI Navigator", link: this.getRoute('features_ai_navigator')},
                {title: "Quick Consult", link: this.getRoute('quick_consult')},
                {title: "Q&A Database", link: this.getRoute('premium_answers')},
                {title: "Compliance Calculators", link: this.getRoute('compliance_calculators')},
                {title: "Revit Add-in", link: this.getRoute('revit')},
                {title: "Webinars", link: this.getRoute('webinars')},
                {title: "Pricing", link: this.getRoute('pricing_page')}
            ],
            favoriteTitles: []
        }
    },
    computed: {
        codeItems() {
            const { Category = [], Location = [] } = this.$store.getters.getSearchNavigationCategories;

            const items = [...Category, ...Location].map(({ name, slug }) => ({
                title: name,
                link: `/codes/${slug}`
            }));

            return getTopLevelOrderedCategories(items, 'title');
        },
        userNotes() {
            return [
                {
                    title: 'Notes and Bookmarks',
                    link: this.getRoute('notes'),
                    authRequired: true,  // Hide if not authenticated
                    restrictForBasic: true, // Disable for Basic users
                    hideForEnterprise: false,
                    tooltip: 'Premium subscription required'
                },
                {
                    title: 'Project Tags',
                    link: this.getRoute('dashboard_my_tags'),
                    authRequired: true,
                    restrictForBasic: true,
                    hideForEnterprise: false,
                    tooltip: 'Premium subscription required'
                },
                {
                    title: 'Sharing History',
                    link: this.getRoute('dashboard_sharing'),
                    authRequired: true,
                    restrictForBasic: true,
                    hideForEnterprise: this.activeEnterpriseProfile, // will be hidden for enterprise user
                    tooltip: 'Premium subscription required'
                }
            ];
        },
        // various auth/subscription states
        isGuestUser() {
            // comes from <app>
            return this.$store.getters.getLoggedIn === "";
        },
        isLoggedIn() {
            return !this.isGuestUser;
        },
        isPremiumUser() {
            return this.hasPremiumSubscription || this.trialActive;
        },
        isBasicUser() {
            return !this.isPremiumUser;
        },
        searchRoute() {
            const section = (this.isActiveUser && this.hasPremiumSubscription) ? 'content' : 'titles';
            return this.getRoute('sections_search_page', { section, searchTermAny: '' });
        }
    },
    watch: {
        menuVisible(newVal) {
            if (!newVal) {
                this.removeFocusTrap();
            }
        }
    },
    methods: {
        closeMenu() {
            this.menuVisible = false;
        },
        getFavoriteBooks() {

            ApiService.getFavourites().then((books) => {
                this.favoriteTitles = (books || []).slice(0, MAX_FAVORITES_COUNT);
                this.favorites = books;
            }).finally(() => {

                this.$nextTick(() => {
                    this.initializeTooltipsFavorites();
                });

            })
        },
        initializeTooltips() {

            document.querySelectorAll('[data-tippy]').forEach((element) => {

                // must be non-empty otherwise we ignore it
                const text = element.getAttribute('data-tippy');

                if (text) {
                    DomUtils.addTooltip(element, text);
                }

            });
        },
        initializeTooltipsFavorites() {

            document.querySelectorAll('.fav-title-item').forEach((element) => {

                const title = element.textContent;

                if (title.length > MAX_FAVORITE_TITLE_LENGTH) {
                    DomUtils.addTooltip(element, title, {
                        placement: 'right'
                    });
                }

            });
        },
        onDrawerTransitionEnd(e) {
            if (this.menuVisible) {
                if (e.target === (this.$refs.navDrawer?.$el || this.$refs.navDrawer)) {
                    const closeEl = this.$refs.closeButton?.$el || this.$refs.closeButton;
                    closeEl?.focus?.();
                    this.trapFocus();
                }
            }
        },
        trapFocus() {
            document.addEventListener('keydown', this.focusTrapHandler);
        },
        removeFocusTrap() {
            document.removeEventListener('keydown', this.focusTrapHandler);
        }
    },
    created() {

        // fetch favorites if logged in
        if (this.isLoggedIn) {
            this.getFavoriteBooks();
            EventBus.listen('reset-favorites', () => this.getFavoriteBooks());
        }

        // can be called from any component basically
        EventBus.listen(EventNames.SHOW_MENU, (options) => {
            this.menuVisible = true;
        });

        Shortcuts.bind('m', () => {
            this.menuVisible = !this.menuVisible;
        });

        Shortcuts.bind('esc', () => {
            this.menuVisible = false;
        });

        this.focusTrapHandler = (e) => {
            if (e.key !== 'Tab') return;

            const drawer = this.$refs.navDrawer?.$el || this.$refs.navDrawer;
            const focusable = drawer.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const focusables = Array.from(focusable).filter(el => !el.disabled && el.offsetParent !== null);

            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    const closeEl = this.$refs.closeButton?.$el || this.$refs.closeButton;
                    closeEl?.focus?.();
                }
            }
        };
    },
    mounted() {
        // will be mounted right away even if not yet visible

        // must wait for all menu elements to render
        this.$nextTick(() => {
            this.initializeTooltips();
        });
    },
    beforeDestroy() {
        this.removeFocusTrap();
    }
}
</script>

<style lang="scss" scoped>
.list-item-with-left-padding {
  padding-left: 72px;
}
</style>

<style lang="scss">
.v-overlay--active {
  z-index: 201;
}
.v-navigation-drawer--temporary {
  z-index: 202;
}
</style>
